import React, { useEffect, useState } from 'react';
import { useAuth } from 'services/auth-service';
import Modal from 'components/elements/Modal';
import * as yup from 'yup';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from 'components/elements/Form/Input/input';
import OrganizationIcon from 'assets/Icons/sidebarIcons/OrganizationIcon';
import { useQuery } from 'react-query';
import { IntegrationDetails, OrganizationProps } from 'services/hooks/organization-service';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import useSaveOrg from './crud/useSave';
import BuildingService from 'services/hooks/building-service';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import PrimaryButton from 'components/elements/Form/button/primaryButton';
import IntegrationService, { IntegrationProps } from 'services/hooks/integration-service';
import { toast } from 'react-toastify';
import {
  generateApiFailErrorMessage,
  generateSaveErrorMessage,
  generateSaveSuccessMessage,
  generateUpdateErrorMessage,
  generateUpdateSuccessMessage
} from 'utils/helpers';
import { LatchKeyTypes, defaultUUIDValue, AccessIntegrationName } from 'utils/constants';
import SelectInput from 'components/elements/Form/select/selectInput';
import { IntegrationKeyProps } from 'services/hooks/integration-key-service';
import LatchService, { LatchBuildingRequest } from 'services/hooks/latch-service';
import { HelpTooltipIcon } from 'components/elements/Help/HelpTooltipIcon';

type CreateProps = {
  entityName: string;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditUUID?: React.Dispatch<React.SetStateAction<string>>;
  createQuery: any;
  updateQuery: any;
  editQuery?: any;
  editUUID?: string;
  queryName: string;
};

declare module 'yup' {
  interface ArraySchema<T> {
    keysFullOrEmpty(message: string): ArraySchema<T>;
  }
}

// Method to validate set of `keys`
yup.addMethod(yup.array, 'keysFullOrEmpty', function (this: yup.ArraySchema<any>, message: string) {
  return this.test('keysFullOrEmpty', message, function (value) {
    const keys = value || [];
    const { path, createError } = this;

    const allKeysFilled = keys.every((key) => key.clientId && key.clientSecret && key.keyType);
    const allKeysEmpty = keys.every((key) => !key.clientId && !key.clientSecret && !key.keyType);

    const valid = allKeysFilled || allKeysEmpty;

    return valid || createError({ path, message });
  });
});

type LatchBuildingOption = {
  value: string;
  label: string;
};

export default function Save({
  entityName,
  showModal,
  setEditUUID,
  setShowModal,
  createQuery,
  updateQuery,
  editQuery,
  editUUID,
  queryName
}: CreateProps) {
  const [getOrgResult, setGetOrgResult] = useState<OrganizationProps | null>(null);
  const [integrationResult, setIntegrationResult] = useState<IntegrationProps | null>(null);
  const [isLatchIntegration, setIsLatchIntegration] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<string | undefined>(undefined);
  const [intPreferenceUuid, setIntPreferenceUuid] = useState<string | null>(null);
  const [latchBuildingOptions, setLatchBuildingOptions] = useState<LatchBuildingOption[]>([]);
  const [isLatchBuildingDisplayed, setIsLatchBuildingDisplayed] = useState(false);
  const [areKeysFilled, setAreKeysFilled] = useState(false);

  const {
    state: { user }
  } = useAuth();

  // validation
  const CreateOrgSchema = yup.object().shape({
    buildingUuid: yup.string().when([], {
      is: () => user.role !== 'BUILDING',
      then: yup.string().required('Building is required'),
      otherwise: yup.string().notRequired()
    }),
    name: yup.string().required('Name is required'),
    integrationUuid: yup.string().required('Integration is required'),
    keys: yup
      .array()
      .keysFullOrEmpty(
        'Please ensure that both sets of keys are completely filled out or leave both sets blank.'
      ),
    latchBuildingUuid: yup.string().when('keys', {
      is: (keys: IntegrationKeyProps[]) =>
        keys &&
        keys.every((key) => key.clientId && key.clientSecret && key.keyType) &&
        latchBuildingOptions.length > 0 &&
        isLatchBuildingDisplayed,
      then: yup.string().required('Latch Building is required'),
      otherwise: yup.string().notRequired()
    }),
    integrationDetails: yup
      .object()
      .shape({
        acsSystemId: yup.string(),
        wavelynxWallet: yup
          .object()
          .shape({
            readerId: yup.string().when('integrationUuid', {
              is: () => integrationResult?.name === AccessIntegrationName.WavelynxWallet,
              then: yup.string().required('Reader ID is required'),
              otherwise: yup.string().notRequired()
            }),
            groupId: yup.number().notRequired()
          })
          .default(undefined),
        ict: yup
          .object()
          .shape({
            email: yup
              .string()
              .email()
              .when('integrationUuid', {
                is: () => integrationResult?.name === AccessIntegrationName.ICT,
                then: yup.string().email().required('ICT portal account email is required'),
                otherwise: yup.string().notRequired()
              }),
            password: yup.string().when('integrationUuid', {
              is: () => integrationResult?.name === AccessIntegrationName.ICT,
              then: yup.string().required('ICT portal account password is required'),
              otherwise: yup.string().notRequired()
            })
          })
          .default(undefined)
      })
      .default(undefined)
  });

  const { isSuccess, data } = useQuery<OrganizationProps, Error>(
    'organization',
    () => editQuery(editUUID),
    {
      enabled: editUUID ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Organization'));
      }
    }
  );

  const methods = useForm({
    defaultValues: getOrgResult
      ? {
          uuid: getOrgResult.uuid,
          name: getOrgResult.name,
          integrationUuid: getOrgResult.integrationUuid,
          integrationDetails: {
            acsSystemId: getOrgResult?.integrationDetails?.acsSystemId || '',
            wavelynxWallet: getOrgResult?.integrationDetails?.wavelynxWallet || {
              readerId: '',
              groupId: undefined
            },
            ict: getOrgResult?.integrationDetails?.ict || {
              email: '',
              password: ''
            }
          },
          keys: getOrgResult?.keys?.length
            ? getOrgResult.keys
            : [
                { clientId: '', clientSecret: '', keyType: '' },
                { clientId: '', clientSecret: '', keyType: '' }
              ],
          buildingUuid: getOrgResult?.buildingUuid,
          latchBuildingUuid: getOrgResult?.integrationBuildingUuid
        }
      : {
          uuid: '',
          name: '',
          integrationUuid: '',
          integrationDetails: {
            acsSystemId: '',
            wavelynxWallet: { readerId: '', groupId: undefined },
            ict: { email: '', password: '' }
          },
          keys: [
            { clientId: '', clientSecret: '', keyType: '' },
            { clientId: '', clientSecret: '', keyType: '' }
          ],
          buildingUuid: '',
          latchBuildingUuid: ''
        },
    resolver: yupResolver(CreateOrgSchema)
  });

  useEffect(() => {
    if (data && editUUID) {
      setGetOrgResult(data);
      methods.reset({
        ...data, // Spread the rest of the data
        keys: data?.keys?.length
          ? data.keys
          : [
              { clientId: '', clientSecret: '', keyType: '' },
              { clientId: '', clientSecret: '', keyType: '' }
            ],
        integrationUuid:
          data?.integrationUuid !== defaultUUIDValue ? data?.integrationUuid : undefined,
        latchBuildingUuid:
          data?.integrationBuildingUuid !== defaultUUIDValue ? data?.integrationBuildingUuid : ''
      });
    }
  }, [data, editUUID, methods]);

  const saveOrgMutation = useSaveOrg({
    createQuery,
    updateQuery,
    queryName,
    onSuccess: () => {
      setShowModal(false);
      if (!editUUID) {
        toast.success(generateSaveSuccessMessage('Organization'));
      } else {
        toast.success(generateUpdateSuccessMessage('Organization'));
      }
      if (setEditUUID) setEditUUID('');
    },
    onError: () => {
      if (!editUUID) {
        toast.error(generateSaveErrorMessage('Organization'));
      } else {
        toast.error(generateUpdateErrorMessage('Organization'));
      }
    }
  });

  const integrationUuidWatch = methods.watch('integrationUuid');

  // Fetch selected integration's data
  const { data: integrationData, refetch: refetchIntegrationData } = useQuery<
    IntegrationProps,
    Error
  >(
    ['integration', integrationUuidWatch],
    () => IntegrationService.findIntegrationById(integrationUuidWatch),
    {
      enabled: integrationUuidWatch ? true : false,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Integration'));
      }
    }
  );

  useEffect(() => {
    // Refetch Integration data when `integrationUuidWatch` changes
    if (integrationUuidWatch) {
      refetchIntegrationData();
    }

    // Set state of integrationData
    if (integrationData) {
      setIntegrationResult(integrationData);
      setSelectedIntegration(integrationData?.name);
    } else {
      /* The below code is setting the state of `integrationResult` to null. This is done when the `integrationUuid` is not selected 
      in the form, so it clears the previous value of `integrationResult`. */
      setIntegrationResult(null);
      setSelectedIntegration(undefined);
    }

    if (integrationResult?.name === AccessIntegrationName.Latch) {
      setIsLatchIntegration(true);
    } else {
      setIsLatchIntegration(false);
    }
  }, [integrationData, integrationResult, integrationUuidWatch, refetchIntegrationData]);

  useEffect(() => {
    // Set selected Integration's Preference UUID
    if (integrationResult && isLatchIntegration) {
      setIntPreferenceUuid(integrationResult?.preferences?.uuid);
    } else {
      setIntPreferenceUuid(null);
    }
  }, [integrationResult, isLatchIntegration]);

  /* The `useFieldArray` hook is used to work with dynamically added fields in a form. In this
  case, it is being used to manage an array of fields with the name 'keys' within the form controlled
  by `methods.control`. */
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'keys'
  });

  // Watch the key values to enable/disable the 'Verify Keys' button
  useEffect(() => {
    const subscription = methods.watch((value) => {
      const allKeysFilled = value.keys
        ? value.keys.every((key) => key && key.clientId && key.clientSecret && key.keyType)
        : false;
      setAreKeysFilled(allKeysFilled);
    });

    return () => subscription.unsubscribe();
  }, [methods]);

  useEffect(() => {
    // Clear latchBuildingOptions and latchBuildingUuid if keys are not filled
    if (!areKeysFilled) {
      setLatchBuildingOptions([]);
      methods.setValue('latchBuildingUuid', '');
      setIsLatchBuildingDisplayed(false);
    }
  }, [areKeysFilled, methods]);

  // This handler adds two pair of Key fields
  const addKeys = () => {
    if (fields.length === 0) {
      append({
        clientId: undefined,
        clientSecret: undefined,
        keyType: undefined
      });
      append({
        clientId: undefined,
        clientSecret: undefined,
        keyType: undefined
      });
    }
  };

  // This handler removes both pair of Key fields
  const removeAllKeys = () => {
    const keyCount = methods.getValues('keys').length;
    for (let i = 0; i < Math.min(2, keyCount); i++) {
      remove(keyCount - 1 - i);
    }
    methods.setValue('latchBuildingUuid', '');
    setLatchBuildingOptions([]);
    setIsLatchBuildingDisplayed(false);
  };

  const verifyKeys = async () => {
    try {
      const keys = methods.getValues('keys');
      const m2mKey = keys.find((key) => key.keyType === 'm2m');
      const passwordlessKey = keys.find((key) => key.keyType === 'passwordless');

      if (m2mKey && passwordlessKey) {
        const request: LatchBuildingRequest = {
          clientId: m2mKey.clientId,
          clientSecret: m2mKey.clientSecret,
          keyType: m2mKey.keyType
        };

        const response = await LatchService.retrieveLatchBuildings(request);

        // Update latchBuildingOptions with the response data
        if (response && response.buildings) {
          const options: LatchBuildingOption[] = response.buildings.map((building) => ({
            value: building.uuid,
            label: building.name
          }));
          setLatchBuildingOptions(options);
          setIsLatchBuildingDisplayed(true);
        }
      } else {
        setLatchBuildingOptions([]);
        setIsLatchBuildingDisplayed(false);
        methods.setValue('latchBuildingUuid', '');

        toast.error('Please provide both m2m and passwordless keys');
      }
    } catch (error: any) {
      // Hide latch building select and reset options on error
      setLatchBuildingOptions([]);
      setIsLatchBuildingDisplayed(false);
      methods.setValue('latchBuildingUuid', '');

      if (error.response && error.response.status === 400) {
        toast.error(
          'Failed to verify keys. Please provide the correct set of m2m and passwordless keys.'
        );
      } else {
        toast.error('An error occurred while verifying keys. Please try again.');
      }
    }
  };

  const onSubmit = async (e: OrganizationProps) => {
    let buildingID = '';
    if (user.role === 'SUPER' || user.role === 'ACCOUNT' || user.role === 'ORG') {
      buildingID = e?.buildingUuid || '';
    } else if (user.role === 'BUILDING') {
      buildingID = user?.uuid ? user.uuid : '';
    }

    const integrationDetails: IntegrationDetails = {
      ...(e?.integrationDetails?.acsSystemId && { acsSystemId: e.integrationDetails.acsSystemId }),
      ...((e?.integrationDetails?.wavelynxWallet?.readerId ||
        e?.integrationDetails?.wavelynxWallet?.groupId) && {
        wavelynxWallet: e.integrationDetails.wavelynxWallet
      }),
      ...((e?.integrationDetails?.ict?.email || e?.integrationDetails?.ict?.password) && {
        ict: e.integrationDetails.ict
      })
    };

    // Conditionally add the preferenceUuid to each key object in the `e?.keys` array
    const formKeys: IntegrationKeyProps[] | undefined = e?.keys?.map((key) => {
      // Check if any of the fields have a value
      const hasValues = key.clientId || key.clientSecret || key.keyType;
      return {
        ...key,
        ...(hasValues && { preferenceUuid: intPreferenceUuid ? intPreferenceUuid : undefined })
      };
    });

    const updatedFormKeys = formKeys?.filter(
      (key) =>
        Object.keys(key).length > 0 && // Ensure the object has keys
        (key.clientId || key.clientSecret || key.keyType || key.preferenceUuid) // Check if properties exist
    );

    const allKeys = updatedFormKeys || [];

    saveOrgMutation.mutate({
      uuid: e?.uuid,
      name: e?.name,
      buildingUuid: buildingID,
      bitformat: e?.bitformat ? e.bitformat : 'W26-Pier',
      integrationUuid: e?.integrationUuid ? e?.integrationUuid : undefined,
      integrationDetails: integrationDetails,
      keys: allKeys,
      integrationBuildingUuid: e?.latchBuildingUuid ? e?.latchBuildingUuid : undefined
    });
  };

  const getButtonLabel = () => {
    if (isLatchBuildingDisplayed) {
      return editUUID ? 'Sync and Update' : 'Sync and Create';
    }
    return entityName;
  };

  return !editUUID || isSuccess ? (
    <>
      <Modal
        isOpen={showModal}
        onRequestClose={() => {
          setShowModal(false);
          if (setEditUUID) setEditUUID('');
        }}
        title={entityName}
      >
        <form onSubmit={methods.handleSubmit(onSubmit)} className={`text-left items-start`}>
          <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
            {(user.role === 'SUPER' || user.role === 'ACCOUNT') && (
              <SelectEntity
                Controller={Controller}
                control={methods.control}
                label="Choose a Building"
                placeholder="Select Building"
                queryname="buildings"
                name="buildingUuid"
                errors={methods.formState.errors}
                disabled={editUUID && true}
                query={BuildingService.findAllBuildings}
                defaultValue=""
              />
            )}
            <Input
              label="Organization Name"
              name="name"
              type="text"
              placeholder="Bline"
              autoFocus
              methods={methods}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <div style={{ flexGrow: 1 }}>
                <SelectEntity
                  Controller={Controller}
                  control={methods.control}
                  label="Choose an Integration"
                  placeholder="Select an Integration"
                  queryname="integrations"
                  name="integrationUuid"
                  errors={methods.formState.errors}
                  query={() => IntegrationService.findAllIntegrations()}
                  expectedLabel="Integration/Name"
                  multi={false}
                  disabled={!!editUUID}
                />
              </div>
              <HelpTooltipIcon message="Once an integration has been assigned to an organization, it cannot be changed. This setting is permanent." />
            </div>

            {/* Assa Visionline specific fields */}
            {selectedIntegration === AccessIntegrationName.AssaVisionline && (
              <Input
                label="Enter ACS System Id"
                name="integrationDetails.acsSystemId"
                type="text"
                placeholder="Enter ACS System Id"
                autoFocus
                methods={methods}
              />
            )}

            {/* Wavelynx Wallet specific fields */}
            {selectedIntegration === AccessIntegrationName.WavelynxWallet && (
              <Input
                label="Reader ID"
                name="integrationDetails.wavelynxWallet.readerId"
                type="text"
                placeholder="Enter Reader ID"
                autoFocus
                methods={methods}
                disabled={!!editUUID}
              />
            )}

            {/* ICT specific fields */}
            {selectedIntegration === AccessIntegrationName.ICT && (
              <>
                <Input
                  label="Portal Account Email"
                  name="integrationDetails.ict.email"
                  type="email"
                  placeholder="sam.smith@b-line.io"
                  autoFocus
                  methods={methods}
                  disabled={!!editUUID}
                />
                <Input
                  label="Portal Account Password"
                  name="integrationDetails.ict.password"
                  type="password"
                  placeholder="**********"
                  methods={methods}
                  autoComplete="new-password"
                />
              </>
            )}
          </div>

          {isLatchIntegration && (
            <div>
              <div className="mt-4 border-t border-b border-neutral12">
                <div className="border-b border-neutral12 pb-4">
                  <div className="text-lg font-semibold mt-4 mb-2">Add Integration Keys</div>
                  <span className="text-sm">
                    These keys will be used to grant access to the Latch doors the occupant needs
                    access to.
                  </span>
                </div>
                {fields.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <div
                      className="grid gap-4 pt-4 border-b border-neutral12"
                      style={{ gridTemplateColumns: 'auto 1fr 1fr 1fr' }}
                    >
                      <div className="text-neutral9 font-semibold text-sm">KEY {index + 1}</div>
                      <Input
                        label="Client ID"
                        name={`keys[${index}].clientId`}
                        type="password"
                        placeholder="Enter Client Id"
                        autoFocus
                        methods={methods}
                        autoComplete="new-password"
                      />
                      <Input
                        label="Client Secret"
                        name={`keys[${index}].clientSecret`}
                        type="password"
                        placeholder="Enter Client Secret"
                        autoFocus
                        methods={methods}
                        autoComplete="new-password"
                      />
                      <SelectInput
                        Controller={Controller}
                        control={methods.control}
                        label="Key Type"
                        name={`keys[${index}].keyType`}
                        errors={methods.formState.errors}
                        placeholder="Select a Key Type"
                        options={LatchKeyTypes}
                      />
                    </div>
                  </React.Fragment>
                ))}
                <div className="mt-4 mb-4">
                  {fields.length >= 2 && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className={`primary-btn px-4 w-auto ${
                          fields.length >= 2 ? '!bg-secondary2' : '!bg-neutral2 cursor-not-allowed'
                        }`}
                        onClick={removeAllKeys}
                        disabled={fields.length < 2}
                      >
                        Remove Keys
                      </button>
                      <button
                        type="button"
                        className={`primary-btn px-4 w-auto ${
                          areKeysFilled ? '!bg-primary1' : '!bg-neutral2 cursor-not-allowed'
                        }`}
                        onClick={verifyKeys}
                        disabled={!areKeysFilled}
                      >
                        Verify Keys
                      </button>
                    </div>
                  )}
                  {fields.length < 2 && (
                    <button
                      type="button"
                      className={`primary-btn default px-4 w-auto ${
                        fields.length < 2 ? '!bg-primary1' : '!bg-neutral2 cursor-not-allowed'
                      }`}
                      onClick={addKeys}
                      disabled={fields.length >= 2}
                    >
                      Add Keys
                    </button>
                  )}
                  {methods.formState.errors.keys && (
                    <div>
                      <span className="error" role="alert">
                        {methods.formState.errors.keys.message}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              {latchBuildingOptions.length > 0 && isLatchBuildingDisplayed && (
                <>
                  <div className="pb-1">
                    <div className="text-lg font-semibold mt-4 mb-2">Assign Keys to Building</div>
                  </div>
                  <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 mr-8'}>
                    <SelectInput
                      Controller={Controller}
                      control={methods.control}
                      label="Choose Latch Building"
                      name="latchBuildingUuid"
                      errors={methods.formState.errors}
                      placeholder="Select Latch Building"
                      options={latchBuildingOptions}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <PrimaryButton
            buttonLabel={getButtonLabel()}
            iconLeft={<OrganizationIcon />}
            variant="default"
            size={'mt-4 px-6 w-auto gap-2'}
          />
        </form>
      </Modal>
    </>
  ) : (
    <LoadingSpinner />
  );
}
